<template>
  <div class="ai-loader-overlay">
    <div class="ai-loader-card">
      <div class="ai-loader-header">
        <div class="ai-loader-title">Performing Assessment from Photos</div>
        <div class="ai-loader-secs">Secs: {{ secondsDisplay }}</div>
      </div>
      <div class="ai-progress">
        <div class="ai-progress-track">
          <div class="ai-progress-fill" :style="{ width: `${clampedWidth}%` }"></div>
        </div>
      </div>
      <div class="ai-blink">
        <span class="ai-blink__text">Analysing photos</span>
        <div class="btn-ai"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiAnalysisLoader',
  props: {
    timeRemaining: {
      type: Number,
      default: 35,
    },
    valueLoader: {
      type: Number,
      default: 0,
    },
    // Extra seconds to add to the displayed timer (added to internal timer)
    timerSeconds: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      currentTimeRemaining: Number.isFinite(this.timeRemaining) ? this.timeRemaining : 0,
      currentValueLoader: Number.isFinite(this.valueLoader) ? this.valueLoader : 0,
      timeInterval: null,
      progressInterval: null,
      rafId: null,
      frameNowMs: 0,
      lastTickTs: 0,
      timer: 0,
    }
  },
  computed: {
    progressPercent() {
      const v = Number.isFinite(this.currentValueLoader) ? this.currentValueLoader : 0
      return Math.min(Math.max(Math.round(v), 0), 100)
    },
    clampedWidth() {
      const v = Number.isFinite(this.currentValueLoader) ? this.currentValueLoader : 0
      return Math.min(Math.max(v, 0), 100)
    },
    secondsRaw() {
      // Base seconds from internal timer plus fractional part since last tick
      const extra = Number.isFinite(this.timerSeconds) ? Number(this.timerSeconds) : 0
      const now = Number(this.frameNowMs) || 0
      const last = Number(this.lastTickTs) || 0
      const frac = now > 0 && last > 0 ? Math.min(Math.max((now - last) / 1000, 0), 0.999) : 0
      return Number(this.timer || 0) + frac + extra
    },
    secondsDisplay() {
      // Show seconds with milliseconds (3 decimals)
      return Number(this.secondsRaw || 0).toFixed(2)
    }
  },
  watch: {
    timeRemaining(newVal) {
      if (Number.isFinite(newVal) && newVal >= 0) {
        this.currentTimeRemaining = newVal
      }
    },
    valueLoader(newVal) {
      if (!Number.isFinite(newVal)) return
      this.currentValueLoader = Math.min(Math.max(newVal, 0), 100)
      if (this.currentValueLoader >= 100) {
        // this.stopTimers()
      }
    }
  },
  mounted() {
    this.startTimers()
  },
  beforeDestroy() {
    this.stopTimers()
  },
  methods: {
    startTimers() {
      this.stopTimers()
      // initialize tick timestamp and start RAF for fractional display
      this.lastTickTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()
      this.startRaf()

      this.timeInterval = setInterval(() => {
        // maintain original timer logic: increment once per second
        this.timer++
        this.lastTickTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()
        if (this.currentTimeRemaining > 0) {
          this.currentTimeRemaining--
        }
      }, 1000)

      this.progressInterval = setInterval(() => {
        if (this.currentValueLoader < 90) {
          const denom = Math.max(this.currentTimeRemaining, 1)
          const step = (90 / denom) * (Math.random() * 0.2 + 0.9)
          this.currentValueLoader = Math.min(this.currentValueLoader + step, 90)
        }
      }, 1000)
    },
    startRaf() {
      const tick = () => {
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()
        this.frameNowMs = now
        this.rafId = requestAnimationFrame(tick)
      }
      this.rafId = requestAnimationFrame(tick)
    },
    stopRaf() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
      this.frameNowMs = 0
    },
    stopTimers() {
      this.stopRaf()
      this.timer = 0
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      if (this.timeInterval) {
        clearInterval(this.timeInterval)
        this.timeInterval = null
      }
    }
  }
}
</script>

<style scoped>
.ai-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-loader-card {
  background: #fff;
  padding: 32px 40px;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  min-width: 520px;
}

.ai-loader-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.ai-loader-title {
  font-size: 20px;
  font-weight: 700;
  color: #3a3a3a;
}

.ai-loader-secs {
  font-size: 12px;
  color: #333;
}

.ai-progress-track {
  width: 100%;
  height: 8px;
  background: #cfcfcf;
  border-radius: 3px;
  overflow: hidden;
}

.ai-progress-fill {
  height: 100%;
  width: 0;
  background: #5970ff;
  transition: width 0.4s ease;
}
.btn-ai {
    width: 46px;
    height: 46px;
    background: url('@/assets/img/ai-icon-new.png') no-repeat center center;
    background-color: white;
    background-size: contain;
}
.ai-blink{
  margin-top: 20px;
  display: flex;
  gap:10px;
  align-items: center;
  justify-content: end;
}

.ai-blink__text {
  font-weight: 700;
  font-size: 20px;
  animation: aiBlink 1s ease-in-out infinite;
}

@keyframes aiBlink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-blink__text {
    animation: none;
  }
}
</style>
